import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { FCC } from "@/types";

export type AnimationTextProp = {
  textString: string;
  textStyle?: string;
  animationStyle?: "typeOnly" | "typeAndDelete";
};

type TypingAnimationProps = {
  content: AnimationTextProp[];
  animationSpeed?: number;
  containerStyle?: string;
  cursorStyle?: string;
  delayBetweenTexts?: number; // Delay between consecutive text animations in ms
};

/**
 * Handles character-by-character animation of text
 */
const animateText = async (
  text: string,
  onUpdate: (text: string) => void,
  animationSpeed: number,
  isDeleting: boolean = false,
) => {
  let current = isDeleting ? text : "";

  while (isDeleting ? current.length > 0 : current.length < text.length) {
    await new Promise((resolve) =>
      setTimeout(resolve, isDeleting ? animationSpeed * 0.5 : animationSpeed),
    );

    current = isDeleting
      ? current.slice(0, -1)
      : current + text[current.length];

    onUpdate(current);
  }

  return current;
};

/**
 * TypingAnimation - A component that animates text with a typewriter effect
 */
export const TypingAnimation: FCC<TypingAnimationProps> = ({
  content,
  animationSpeed = 100,
  delayBetweenTexts = 1000,
}) => {
  const [currentText, setCurrentText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  // Lock the content array once to avoid re-runs
  const frozenContentRef = useRef<AnimationTextProp[]>(content);

  const currentContent = frozenContentRef.current[currentIndex];

  useEffect(() => {
    if (!frozenContentRef.current || frozenContentRef.current.length === 0)
      return;

    let isMounted = true;

    const runAnimation = async () => {
      const contentArray = frozenContentRef.current;
      const { textString, animationStyle = "typeOnly" } =
        contentArray[currentIndex];

      if (!isMounted) return;

      await animateText(textString, setCurrentText, animationSpeed);

      if (!isMounted) return;

      if (animationStyle === "typeAndDelete") {
        await new Promise((resolve) => setTimeout(resolve, delayBetweenTexts));
        if (!isMounted) return;
        await animateText(textString, setCurrentText, animationSpeed, true);
      }

      if (!isMounted) return;

      if (currentIndex < contentArray.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayBetweenTexts));
        if (!isMounted) return;
        setCurrentIndex((prev) => prev + 1);
      }
    };

    runAnimation();

    return () => {
      isMounted = false;
    };
  }, [currentIndex, animationSpeed, delayBetweenTexts]);

  return (
    <div className="flex min-h-48 w-full flex-col items-start md:min-h-72 md:pt-20">
      <h1
        style={{ wordBreak: "break-word" }}
        className={clsx(currentContent?.textStyle || "hero font-bold")}
      >
        <span className="inline-block">
          <span>{currentText}</span>
          <span
            className="inline-block h-[1em] w-[8px] animate-blink bg-current"
            aria-hidden="true"
          />
        </span>
      </h1>
    </div>
  );
};
